import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import i18next from "src/i18n/config";
import { useSelector } from "src/store";
import { makeStyles } from "tss-react/mui";

import { Group } from "@dashboard/devices/types/index";
import { selectGroups } from "@dashboard/groups/store/selectors";
import { getGroupsForAggregation } from "@dashboard/groups/utils";
import { TableContainer, TableRow, Theme } from "@mui/material";
import { SectionPanel } from "@shared/SectionPanel/SectionPanel";
import { HoverRow } from "@shared/table/HoverRow";
import { MinimalTable, MinimalTableCellWithTooltip, MinimalTableHeader } from "@shared/table/MinimalTableComponents";
import { TableBody } from "@shared/table/TableBody";
import { Header } from "@shared/table/TableHeader";
import CustomTablePagination from "@shared/table/TablePagination";
import {
    createColumnHelper,
    flexRender,
    getCoreRowModel,
    getPaginationRowModel,
    getSortedRowModel,
    SortingState,
    useReactTable,
} from "@tanstack/react-table";

import DevicesTableItem from "./TableItem";

const useStyles = makeStyles()((muiTheme: Theme) => ({
    nameColumn: { width: "50%" },
    versionColumn: { width: "30%" },
    countColumn: { width: "20%" },
    container: { height: "100%" },
    table: {
        flex: 1,
        minHeight: 0,
    },
    pagination: {
        marginTop: "auto",
        flexShrink: 0,
        borderTop: `1px solid ${muiTheme.palette.emteria.border.secondary}`,
    },
}));

type ProductWithCount = {
    codename: string;
    productName?: string;
    count: number;
};

const columnHelper = createColumnHelper<ProductWithCount>();

const getProductLabel = (product: ProductWithCount): string => product.productName || product.codename;

const getColumns = () => {
    return [
        columnHelper.accessor((row) => getProductLabel(row), {
            id: "product",
            header: i18next.t("common:label.product"),
            cell: (info) => <MinimalTableCellWithTooltip title={info.getValue<string>()} style={{ maxWidth: "100%" }} />,
        }),
        columnHelper.accessor("codename", {
            id: "codename",
            header: i18next.t("pages:overviewPage.table.header.codename"),
            cell: (info) => <MinimalTableCellWithTooltip title={info.getValue<string>()} style={{ maxWidth: "100%" }} />,
        }),
        columnHelper.accessor("count", {
            id: "count",
            header: i18next.t("pages:overviewPage.table.header.devices"),
            cell: (info) => {
                const codename = info.row.original.codename;
                const count = info.getValue<number>();
                const filteredGroups = info.table.options.meta?.filteredGroupIds || [];
                return <DevicesTableItem count={count} codename={codename} selectedGroupIds={filteredGroups} />;
            },
            meta: {
                align: "right",
            },
        }),
    ];
};

const compareProducts = (a: ProductWithCount, b: ProductWithCount): number => {
    if (a.count !== b.count) {
        return b.count - a.count;
    }

    return getProductLabel(a).localeCompare(getProductLabel(b)) || a.codename.localeCompare(b.codename);
};

const aggregateProductCountsFromGroups = (groups: Group[], groupIds: number[]): ProductWithCount[] => {
    const targetGroups = getGroupsForAggregation(groupIds, groups);

    const products: Record<string, ProductWithCount> = {};
    targetGroups.forEach((group) => {
        (group.statistics?.products ?? []).forEach(({ codename, count, productName }) => {
            const existing = products[codename];
            products[codename] = {
                codename,
                productName: existing?.productName || productName,
                count: (existing?.count || 0) + count,
            };
        });
    });

    return Object.values(products).sort(compareProducts);
};

type Props = {
    groupIds: number[];
};

export const ProductsCard = ({ groupIds }: Props) => {
    const { classes } = useStyles();
    const { t } = useTranslation(["pages", "common"]);
    const [sorting, setSorting] = useState<SortingState>([{ id: "count", desc: true }]);

    const groups = useSelector(selectGroups);
    const filteredGroups = !groupIds.length ? groups.map((group) => group.id) : groupIds;

    const productCounts = React.useMemo(() => aggregateProductCountsFromGroups(groups, filteredGroups), [groups, filteredGroups]);

    const table = useReactTable<ProductWithCount>({
        data: productCounts,
        columns: getColumns(),
        initialState: {
            pagination: {
                pageSize: 8,
            },
        },
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
        meta: {
            filteredGroupIds: filteredGroups,
        },
    });

    const { pageSize, pageIndex } = table.getState().pagination;

    return (
        <SectionPanel disablePadding className={classes.container}>
            <TableContainer className={classes.table}>
                <MinimalTable stickyHeader sx={{ tableLayout: "fixed" }}>
                    <colgroup>
                        <col className={classes.nameColumn} />
                        <col className={classes.versionColumn} />
                        <col className={classes.countColumn} />
                    </colgroup>
                    <MinimalTableHeader
                        title={{
                            label: t("pages:overviewPage.cards.products.title"),
                            colSpan: table.getVisibleLeafColumns().length,
                        }}
                    >
                        {table.getHeaderGroups().map((headerGroup) => (
                            <TableRow key={headerGroup.id}>
                                {headerGroup.headers.map((header) => (
                                    <Header key={header.id} {...header} />
                                ))}
                            </TableRow>
                        ))}
                    </MinimalTableHeader>
                    <TableBody<ProductWithCount> table={table} noDataMessage={t("common:table.noDevices")}>
                        {table.getRowModel().rows.map((row) => (
                            <HoverRow key={row.id} {...row} clickable={false}>
                                {row.getVisibleCells().map((cell) =>
                                    flexRender(cell.column.columnDef.cell, {
                                        ...cell.getContext(),
                                        key: cell.id,
                                    }),
                                )}
                            </HoverRow>
                        ))}
                    </TableBody>
                </MinimalTable>
            </TableContainer>
            <CustomTablePagination
                component="div"
                className={classes.pagination}
                rowsPerPageOptions={[8, 24, 48]}
                count={table.getFilteredRowModel().rows.length}
                rowsPerPage={pageSize}
                page={pageIndex}
                onPageChange={(_event, page) => table.setPageIndex(page)}
                onRowsPerPageChange={(e) => {
                    const size = e.target.value ? Number(e.target.value) : 8;
                    table.setPageSize(size);
                }}
            />
        </SectionPanel>
    );
};
